import { useState, type ReactNode } from 'react';
import { Page } from 'konsta/react';
import { HandHeart, Search } from 'lucide-react';
import { api, type Me } from '../api';
import { haptic } from '../telegram';
import { useT } from '../i18n';

/**
 * Client or Provider. Agents and admins are created by admin, never chosen here.
 * `agentCode` (from an agent invite link) is carried into provider registration in Phase 2.
 */
export function RoleScreen({ onChosen }: { onChosen: (me: Me) => void; agentCode: string | null }) {
  const t = useT();
  const [busy, setBusy] = useState(false);

  const choose = async (role: 'CLIENT' | 'PROVIDER') => {
    haptic();
    setBusy(true);
    try {
      onChosen(await api.chooseRole(role));
    } finally {
      setBusy(false);
    }
  };

  const Card = ({ role, icon, title, desc }: { role: 'CLIENT' | 'PROVIDER'; icon: ReactNode; title: string; desc: string }) => (
    <button
      disabled={busy}
      onClick={() => choose(role)}
      className="flex w-full items-center gap-4 rounded-2xl bg-tg-secondary p-5 text-left active:opacity-70 disabled:opacity-50"
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-tg-button text-tg-buttonText">{icon}</span>
      <span className="flex flex-col">
        <span className="text-lg font-semibold">{title}</span>
        <span className="text-sm text-tg-hint">{desc}</span>
      </span>
    </button>
  );

  return (
    <Page className="flex flex-col justify-center px-5">
      <h1 className="mb-6 text-center text-2xl font-semibold">{t('app.choose_role')}</h1>
      <div className="space-y-3">
        <Card role="CLIENT" icon={<Search size={24} />} title={t('role.CLIENT')} desc={t('app.role_client')} />
        <Card role="PROVIDER" icon={<HandHeart size={24} />} title={t('role.PROVIDER')} desc={t('app.role_provider')} />
      </div>
    </Page>
  );
}
